/**
 * TanStack Query hooks for invitation code management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminInvitationCodesService } from '@/services/admin-invitation-codes.service'
import type { InvitationCodeListParams, CreateInvitationCodeRequest } from '@/types/invitation-code'

/**
 * Query keys for invitation code management
 */
export const invitationCodeKeys = {
  all: ['admin', 'invitation-codes'] as const,
  lists: () => [...invitationCodeKeys.all, 'list'] as const,
  list: (params: InvitationCodeListParams) => [...invitationCodeKeys.lists(), params] as const,
  details: () => [...invitationCodeKeys.all, 'detail'] as const,
  detail: (id: string) => [...invitationCodeKeys.details(), id] as const,
}

/**
 * Get paginated list of invitation codes with filters
 */
export function useInvitationCodes(params: InvitationCodeListParams) {
  return useQuery({
    queryKey: invitationCodeKeys.list(params),
    queryFn: () => adminInvitationCodesService.list(params),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  })
}

/**
 * Get invitation code detail by ID
 */
export function useInvitationCode(id: string) {
  return useQuery({
    queryKey: invitationCodeKeys.detail(id),
    queryFn: () => adminInvitationCodesService.getById(id),
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    enabled: !!id,
  })
}

/**
 * Create new invitation code
 */
export function useCreateInvitationCode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateInvitationCodeRequest) =>
      adminInvitationCodesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationCodeKeys.lists() })
      toast.success('Invitation code created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create invitation code', {
        description: error.message,
      })
    },
  })
}

/**
 * Revoke invitation code
 */
export function useRevokeInvitationCode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (codeId: string) => adminInvitationCodesService.revoke(codeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationCodeKeys.all })
      toast.success('Invitation code revoked successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to revoke invitation code', {
        description: error.message,
      })
    },
  })
}
